/* @flow */
import * as React from 'react';

import { computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';

import RequireForAccess from 'react-access';

import { createNewLocation } from 'permissions/account';
import { updateFlagSaleRent } from 'permissions/asset';
import { manageCaaSPermissions } from 'permissions/caas';

import AutoCompleteModel from 'models/forms/AutoCompleteModel.js';

import ElectroTextField from 'forms/components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelect from 'forms/components/inputs/ElectroSelect/ElectroSelect';
import ElectroSelectExtra from 'forms/components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroDatePicker from 'forms/components/inputs/ElectroDatePicker/ElectroDatePicker';
import ElectroCheckbox from 'forms/components/inputs/ElectroCheckbox/ElectroCheckbox';
import ElectroRadioGroup from 'forms/components/inputs/ElectroRadioGroup/ElectroRadioGroup';
import ElectroNumberField from 'forms/components/inputs/ElectroNumberField/ElectroNumberField';
import ElectroResponseMessage from 'forms/components/inputs/ElectroResponseMessage';
import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from 'components/FormSection/FormSection';

import getCurrencySymbol from 'utils/getCurrencySymbol';
import { getCode } from 'utils/statuses';

import {
  AddNewBuilding,
  AddNewCompany,
  AddNewManufacturer,
  AddNewProduct,
  AddNewRoom,
  AddNewSite,
  AddNewSystem,
} from 'views/ActionDrawer';

import FormControls from 'forms/components/controls/FormControls/FormControls';

import {
  autoCompleteAccountCompanies,
  autoCompleteAccountUsers,
  autoCompleteBuildings,
  autoCompleteCompanySites,
  autoCompleteCurrencies,
  autoCompleteFinancialAuditors,
  autoCompleteManufacturers,
  autoCompleteParentAssets,
  autoCompleteProducts,
  autoCompleteTradingAccounts,
} from 'api/forms.js';

import { assetCreate, assetEdit, makeFields } from './setUpSingleAssetForm';

const actionsListData: any = [
  {
    label: 'general:actions.new_manufacturer',
    icon: '#icon-add',
    action: 'newManufacturer',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_product',
    icon: '#icon-add',
    action: 'newProduct',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_company',
    icon: '#icon-add',
    action: 'newCompany',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_site',
    icon: '#icon-add',
    action: 'newSite',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_building_transport',
    icon: '#icon-add',
    action: 'newBuilding',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_lab_room',
    icon: '#icon-add',
    action: 'newRoom',
    permissions: createNewLocation,
  },
  {
    label: 'general:actions.new_system_workspace',
    icon: '#icon-add',
    action: 'newSystem',
    permissions: createNewLocation,
  },
];

type TypeProps = {|
  mode: string,
  assetData: Object,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore', 'accountStore', 'assetStore', 'locationChooserStore')
@observer
export default class SingleAssetForm extends React.Component<*> {
  /*eslint-disable max-len*/

  @observable accountsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteTradingAccounts);

  @observable manufacturerModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteManufacturers
  );

  @observable modelProduct: AutoCompleteModel = new AutoCompleteModel(autoCompleteProducts);

  @observable companyModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAccountCompanies()
  );

  @observable sitesModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteCompanySites);

  @observable buildingsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);

  @observable roomsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);

  @observable systemsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);

  @observable usersModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteAccountUsers);

  @observable parentAssetsModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteParentAssets
  );

  @observable currenciesModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteCurrencies);

  @observable financialAuditorsModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteFinancialAuditors
  );

  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
  };

  @computed get form(): Object {
    const assetData: Object =
      typeof this.props.assetData === 'undefined' ? {} : this.props.assetData;
    const fields: Array<*> = makeFields(assetData, this.props.mode, this.props.t);

    return this.props.mode === 'edit'
      ? assetEdit(fields, assetData, this.localState, this.props.t)
      : assetCreate(fields, this.localState, this.props.t);
  }

  handleChangeSafety = (e: *) => {
    e.target.value === 'true'
      ? this.form.$('calibrationDetails.safetyTested').set(true)
      : this.form.$('calibrationDetails.safetyTested').set(false);
  };

  /*
    handleInputChange = (selectorFiles: FileList, vField: Object) => {
        selectorFiles
            ?
            vField.value = selectorFiles[0].name
            :
            vField.value = '';
    };
    */

  checkReturnCustomField(vField: String, idx: number) {
    const form: Object = this.form;
    const accountsModel: Object = this.accountsModel;
    const status = this.props?.assetData?.status;
    const isRentalOrLease = status
      ? getCode(status) === 'rental' ||
        getCode(status) === 'lease' ||
        getCode(status) === 'on_lease'
      : false;

    if (form.$('chooseAccount.tradingAccount').extra.selectID && accountsModel.chosen) {
      if (
        accountsModel?.chosen?.custom_fields[idx] &&
        accountsModel?.chosen?.custom_fields[idx].key
      ) {
        return (
          <ElectroTextField
            field={form.$(`customFields.${vField}`)}
            disabled={isRentalOrLease}
            props={{
              disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
            }}
          />
        );
      }
    } else if (
      this.props.assetData &&
      this.props.assetData?.custom_fields[idx] &&
      this.props.assetData?.custom_fields[idx].label
    ) {
      return (
        <ElectroTextField
          field={form.$(`customFields.${vField}`)}
          disabled={isRentalOrLease}
          props={{
            disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
          }}
        />
      );
    }

    return null;
  }

  // hasCustomFields() {
  //   const hasFields =
  //     this.checkReturnCustomField('customField1', 0) === null &&
  //     this.checkReturnCustomField('customField2', 1) === null &&
  //     this.checkReturnCustomField('customField3', 2) === null &&
  //     this.checkReturnCustomField('customField4', 3) === null;
  //   return !hasFields;
  // }

  checkReturnAvailableFor() {
    const form: Object = this.form;
    const assetData: Object = this.props.assetData;
    const accountsModel: Object = this.accountsModel;

    if (
      form.$('chooseAccount.tradingAccount').extra.selectID &&
      accountsModel.chosen &&
      (accountsModel.chosen.sale_consignment || accountsModel.chosen.subrental_program)
    ) {
      return (
        <RequireForAccess permissions={updateFlagSaleRent}>
          <FormSection form={form} section="assetAvailableFor">
            <form>
              {accountsModel.chosen.sale_consignment && (
                <ElectroCheckbox field={form.$('assetAvailableFor.saleConsignment')} />
              )}
              <br />
              {accountsModel.chosen.subrental_program && (
                <ElectroCheckbox field={form.$('assetAvailableFor.subrentalProgram')} />
              )}
            </form>
          </FormSection>
        </RequireForAccess>
      );
    } else if (
      assetData &&
      (assetData.account.sale_consignment || assetData.account.subrental_program)
    ) {
      return (
        <RequireForAccess permissions={updateFlagSaleRent}>
          <FormSection form={form} section="assetAvailableFor">
            <form>
              {assetData.account.sale_consignment && (
                <ElectroCheckbox field={form.$('assetAvailableFor.saleConsignment')} />
              )}
              <br />
              {assetData.account.subrental_program && (
                <ElectroCheckbox field={form.$('assetAvailableFor.subrentalProgram')} />
              )}
            </form>
          </FormSection>
        </RequireForAccess>
      );
    }

    return null;
  }

  updateCustomFields() {
    const form: Object = this.form;
    const accountsModel: Object = this.accountsModel;

    for (let i: * = 0; i < 4; i++) {
      if (accountsModel?.chosen?.custom_fields[i] && accountsModel?.chosen?.custom_fields[i].key) {
        form
          .$(`customFields.customField${i + 1}`)
          .set('label', accountsModel.chosen.custom_fields[i].key);
        form.$(`customFields.customField${i + 1}`).set('extra', {
          helperText: accountsModel.chosen.custom_fields[i].value,
          selectID: accountsModel.chosen.custom_fields[i].id,
        });
      }
    }
  }

  resetLocations() {
    const resetModels = [
      this.companyModel,
      this.sitesModel,
      this.buildingsModel,
      this.roomsModel,
      this.systemsModel,
      this.modelProduct,
    ];
    const resetFields = [
      this.form.$('location.locationCompany'),
      this.form.$('location.locationSite'),
      this.form.$('location.locationBuilding'),
      this.form.$('location.locationRoom'),
      this.form.$('location.locationSystem'),
      this.form.$('assetDetail.manufacturer'),
      this.form.$('assetDetail.model'),
    ];

    // Reset Models
    for (let i: * = 0; i < resetModels.length; i++) {
      resetModels[i].clear();
      resetModels[i].loadData();
    }

    // Reset Fields
    for (let i: * = 0; i < resetFields.length; i++) {
      resetFields[i].value = '';
      resetFields[i].extra.selectID = '';
    }
  }

  isInternalExtra(option: Object) {
    const assetData: Object = this.props.assetData ? this.props.assetData : {};
    const form: Object = this.form;

    return (
      form.$('assetRelationship.relationship').value === 'internal' &&
      option.id === 'internal' && (
        <ElectroSelectExtra
          field={form.$('assetRelationship.parentAsset')}
          form={form}
          formName="assetRelationship"
          labelKey={['asset_number', 'manufacturer', 'model']}
          labelSeparator=", "
          exclude={[assetData.id]}
          props={{
            model: this.parentAssetsModel,
          }}
        />
      )
    );
  }

  /**
   * Checks if the form should display the CaaS fields based on the selected
   * Trading Account and Asset configuration.
   */
  shouldShowCaaSSection(): boolean {
    const selectedTradingAccountID = this.form.$('chooseAccount.tradingAccount').extra?.selectID;
    const selectedTradingAccount = this.accountsModel.list.find(
      tradingAccount => tradingAccount.id === selectedTradingAccountID
    );

    return this.assetSupportsCaaS(selectedTradingAccount);
  }

  /**
   * Checks if the Asset supports CaaS for the given Trading Account.
   */
  assetSupportsCaaS(tradingAccount): boolean {
    // These are the rules to check for displaying the CaaS form fields, all
    // rules must be an expression that evaluates to `true` in order to
    // show CaaS fields.
    const rules = [tradingAccount?.is_caas_active, tradingAccount?.region_name === 'AMERICAS'];

    //
    // Additional rules when on edit mode goes here:
    //
    // Rule: When on edit mode, the asset must be an Owned Asset.
    if (this.props.mode === 'edit') {
      rules.push(this.props.assetData?.owned);
    }

    return rules.every(Boolean);
  }

  /**
   * Handles the event when the user selects a Trading Account.
   */
  handleTradingAccountOnChange = tradingAccount => {
    // Make sure to set is_caas_asset field to false if the Asset
    // no longer supports CaaS for the selected Trading Account.
    if (!this.assetSupportsCaaS(tradingAccount)) {
      this.form.$('caas.is_caas_asset').set(false);
    }
  };

  render(): React.Element<*> {
    const form: Object = this.form;
    const assetData: Object = this.props.assetData ? this.props.assetData : {};
    // const accountId: string = this.props.params.accountId;
    const accountsModel: Object = this.accountsModel;
    const manufacturerModel: Object = this.manufacturerModel;
    const modelProduct: Object = this.modelProduct;
    const companyModel: Object = this.companyModel;
    const sitesModel: Object = this.sitesModel;
    const buildingsModel: Object = this.buildingsModel;
    const roomsModel: Object = this.roomsModel;
    const systemsModel: Object = this.systemsModel;
    const usersModel: Object = this.usersModel;
    const parentAssetsModel: Object = this.parentAssetsModel;

    const activeUser: Object = JSON.parse(localStorage.getItem('user'));
    const userRole = activeUser.role_id;

    const isCaasAsset: boolean = form.$('caas.is_caas_asset').value;
    /** Checks if the active user role has permission to manage CaaS */
    const hasCaasPermissions = manageCaaSPermissions.includes(userRole);
    const caasFieldsDisabled = !isCaasAsset || !hasCaasPermissions;

    const t: Function = this.props.t;
    // Price formatting separators, checks for 1,234,567.89
    // This will need revisiting if any other formats are added
    const thousandSeparator: string = activeUser.price_format.match(/^\d+(,\d{3})*(\.\d{1,2})?$/g)
      ? ','
      : '.';
    const decimalSeparator: string = activeUser.price_format.match(/^\d+(,\d{3})*(\.\d{1,2})?$/g)
      ? '.'
      : ',';

    let showFullForm = false;
    // define if form should be full for 'new' and 'owned asset' or partial for 'rented'
    if (!this.props.mode || (this.props.mode === 'edit' && assetData.owned)) showFullForm = true;

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <ElectroResponseMessage localState={this.localState} />
          <FormWrapper form={form}>
            {/* ================| CHOOSE ACCOUNT |================ */}
            <FormSection form={form} section="chooseAccount">
              <ElectroSelectExtra
                field={form.$('chooseAccount.tradingAccount')}
                form={form}
                formName="chooseAccount"
                labelKey="name"
                // updateCustomFields={() => this.updateCustomFields()}
                resetModels={() => this.resetLocations()}
                disabled={!assetData.owned && this.props.mode === 'edit'}
                props={{
                  model: accountsModel,
                  modelChild: [manufacturerModel, companyModel, usersModel, parentAssetsModel],
                }}
                handleOnChange={this.handleTradingAccountOnChange}
              />
            </FormSection>
            {/* ================| ASSET DETAIL |================ */}
            {/* in edit mode, will not show ASSET DETAIL SECTION */}
            {showFullForm && (
              <FormSection form={form} section="assetDetail">
                <React.Fragment>
                  <ElectroTextField field={form.$('assetDetail.assetNumber')} />
                  <ElectroTextField field={form.$('assetDetail.barcodeNumber')} />
                  <ElectroSelectExtra
                    field={form.$('assetDetail.manufacturer')}
                    form={form}
                    formName="assetDetail"
                    labelKey="name"
                    extraFilters={['aliases']}
                    actionButtonData={{
                      data: actionsListData[0],
                      disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
                    }}
                    props={{
                      model: manufacturerModel,
                      modelChild: modelProduct,
                    }}
                  />
                  <ElectroSelectExtra
                    field={form.$('assetDetail.model')}
                    form={form}
                    formName="assetDetail"
                    labelKey="model"
                    actionButtonData={{
                      data: actionsListData[1],
                      disabled: !form.$('assetDetail.manufacturer').extra.selectID,
                    }}
                    props={{
                      model: modelProduct,
                    }}
                  />
                  <ElectroTextField
                    field={this.form.$('assetDetail.modelDescription')}
                    disabled={true}
                    props={{
                      disabled: true,
                      multiline: true,
                      rows: 5,
                    }}
                  />
                  <ElectroTextField field={form.$('assetDetail.serialNumber')} />
                  <ElectroTextField field={form.$('assetDetail.optionsFitted')} />
                  <ElectroSelect
                    field={form.$('assetDetail.currentCondition')}
                    searchable={false}
                    simpleValue={true}
                  />
                  <ElectroSelect
                    field={form.$('assetDetail.assetStatus')}
                    searchable={false}
                    simpleValue={true}
                  />
                  <ElectroTextField field={form.$('assetDetail.softwareVersion')} />
                  <ElectroTextField field={form.$('assetDetail.firmwareVersion')} />
                  <ElectroDatePicker field={form.$('assetDetail.purchasedDate')} />
                </React.Fragment>
              </FormSection>
            )}

            {/* ================| CALIBRATION AS A SERVICE |================ */}
            {this.shouldShowCaaSSection() && (
              <FormSection form={form} section="caas">
                <ElectroCheckbox
                  field={form.$('caas.is_caas_asset')}
                  disabled={!hasCaasPermissions}
                />
                {isCaasAsset && (
                  <>
                    <br />
                    <ElectroCheckbox
                      field={form.$('caas.is_rental_backfill')}
                      disabled={caasFieldsDisabled}
                    />
                    <ElectroSelect
                      field={form.$('caas.vendor')}
                      searchable={false}
                      simpleValue={true}
                      disabled={caasFieldsDisabled}
                    />
                    <ElectroSelect
                      field={form.$('caas.type')}
                      searchable={false}
                      simpleValue={true}
                      disabled={caasFieldsDisabled}
                    />
                    <ElectroSelect
                      field={form.$('caas.currency')}
                      searchable={false}
                      simpleValue={true}
                      disabled={caasFieldsDisabled}
                    />
                    <ElectroNumberField
                      field={form.$('caas.price')}
                      disabled={caasFieldsDisabled}
                      decimalSeparator={decimalSeparator}
                      thousandSeparator={thousandSeparator}
                      prefix={getCurrencySymbol(form.$('caas.currency').value, 'usd')}
                    />
                    <ElectroTextField
                      field={form.$('caas.observations')}
                      disabled={caasFieldsDisabled}
                    />
                  </>
                )}
              </FormSection>
            )}

            {/* ================| CUSTOM FIELDS |================ */}
            {/*{// in edit mode, if it's rented and no Custom Fields are set remove CUSTOM FIELD SECTION*/}
            {/*this.hasCustomFields() && (*/}
            {/*  <FormSection form={form} section="customFields">*/}
            {/*    <React.Fragment>*/}
            {/*      {this.checkReturnCustomField('customField1', 0)}*/}
            {/*      {this.checkReturnCustomField('customField2', 1)}*/}
            {/*      {this.checkReturnCustomField('customField3', 2)}*/}
            {/*      {this.checkReturnCustomField('customField4', 3)}*/}
            {/*    </React.Fragment>*/}
            {/*  </FormSection>*/}
            {/*)}*/}
            {/* ================| CALIBRATION DETAILS |================ */}
            {showFullForm && (
              <React.Fragment>
                <FormSection form={form} section="calibrationDetails">
                  <ElectroSelect
                    field={form.$('calibrationDetails.calibrationStatus')}
                    searchable={false}
                    simpleValue={true}
                  />
                  {form.$('calibrationDetails.calibrationStatus').value !== 'Not Required' && (
                    <React.Fragment>
                      <ElectroDatePicker
                        field={form.$('calibrationDetails.lastCalibrated')}
                        maxDate={new Date()}
                      />
                      <ElectroDatePicker field={form.$('calibrationDetails.calibrationDueDate')} />
                      {/* <ElectroSelect field={form.$('calibrationDetails.calibrationInterval')} searchable={false} simpleValue={true} /> */}
                      <ElectroTextField field={form.$('calibrationDetails.calibrationInterval')} />
                      <ElectroDatePicker
                        field={form.$('calibrationDetails.calibrationPlannedDate')}
                      />
                      <ElectroDatePicker field={form.$('calibrationDetails.inServiceDate')} />
                      <ElectroRadioGroup
                        field={this.form.$('calibrationDetails.safetyTested')}
                        inline={true}
                        inputLabel={true}
                      />
                    </React.Fragment>
                  )}
                </FormSection>
              </React.Fragment>
            )}
            {/* ================| ASSET LOCATION |================ */}
            <FormSection form={form} section="location">
              <ElectroSelectExtra
                field={form.$('location.locationCompany')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  data: actionsListData[2],
                  disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
                }}
                props={{
                  model: companyModel,
                  modelChild: sitesModel,
                }}
              />
              <ElectroSelectExtra
                field={form.$('location.locationSite')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  data: actionsListData[3],
                  disabled: !form.$('location.locationCompany').extra.selectID,
                }}
                props={{
                  model: sitesModel,
                  modelChild: buildingsModel,
                }}
              />
              <ElectroSelectExtra
                field={form.$('location.locationBuilding')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  data: actionsListData[4],
                  disabled: !form.$('location.locationSite').extra.selectID,
                }}
                props={{
                  model: buildingsModel,
                  modelChild: roomsModel,
                }}
              />
              <ElectroSelectExtra
                field={form.$('location.locationRoom')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  data: actionsListData[5],
                  disabled: !form.$('location.locationBuilding').extra.selectID,
                }}
                props={{
                  model: roomsModel,
                  modelChild: systemsModel,
                }}
              />
              <ElectroSelectExtra
                field={form.$('location.locationSystem')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  data: actionsListData[6],
                  disabled: !form.$('location.locationRoom').extra.selectID,
                }}
                props={{
                  model: systemsModel,
                }}
              />
            </FormSection>
            {/* ================| ASSET RELATIONSHIP |================ */}
            {(assetData.owned || typeof this.props.assetData === 'undefined') && (
              <React.Fragment>
                <FormSection form={form} section="assetRelationship">
                  <h5>{t('general:assets.labels.select_relationship')}</h5>
                  <ElectroRadioGroup
                    field={form.$('assetRelationship.relationship')}
                    secondaryKey="hint"
                    customComp={option => this.isInternalExtra(option)}
                  />
                </FormSection>
              </React.Fragment>
            )}
            {/* ================| ASSIGN ASSET |================ */}
            <FormSection form={form} section="assignAsset">
              <ElectroSelectExtra
                field={form.$('assignAsset.assignee')}
                form={form}
                formName="assignAsset"
                labelKey={['first_name', 'last_name']}
                props={{
                  model: usersModel,
                }}
              />
            </FormSection>
            {/* ================| ASSET AVAILABLE FOR |================ */}
            {(assetData.owned || typeof this.props.assetData === 'undefined') &&
              this.checkReturnAvailableFor()}
            {/* ================| FINANCIAL AUDIT|================ */
            assetData.owned && typeof this.props.assetData !== 'undefined' && (
              <FormSection form={form} section="financialAudit">
                <ElectroDatePicker field={form.$('financialAudit.dateOfPurchase')} />
                <ElectroTextField field={form.$('financialAudit.financialAccountNo')} />
                <ElectroTextField field={form.$('financialAudit.departmentCode')} />
                <ElectroDatePicker field={form.$('financialAudit.assetInServiceDate')} />
                <ElectroTextField field={form.$('financialAudit.lifeCyclePeriod')} />
                <ElectroTextField field={form.$('financialAudit.supplier')} />
                <ElectroTextField field={form.$('financialAudit.poNumber')} />

                <ElectroNumberField
                  field={form.$('financialAudit.purchasePrice')}
                  prefix={getCurrencySymbol(
                    form.$('financialAudit.currency').value,
                    activeUser.currency
                  )}
                  thousandSeparator={thousandSeparator}
                  decimalSeparator={decimalSeparator}
                />
                <ElectroNumberField
                  field={form.$('financialAudit.netBookValue')}
                  prefix={getCurrencySymbol(
                    form.$('financialAudit.currency').value,
                    activeUser.currency
                  )}
                  thousandSeparator={thousandSeparator}
                  decimalSeparator={decimalSeparator}
                />
                <ElectroNumberField
                  field={form.$('financialAudit.fairMarketValue')}
                  prefix={getCurrencySymbol('', 'dollars')}
                  thousandSeparator={thousandSeparator}
                  decimalSeparator={decimalSeparator}
                />
                <ElectroSelect
                  field={form.$('financialAudit.currency')}
                  model={this.currenciesModel}
                  simpleValue={true}
                />
                <ElectroTextField field={form.$('financialAudit.warrantyPeriod')} />
                <ElectroDatePicker field={form.$('financialAudit.warrantyExpires')} />
                <ElectroDatePicker field={form.$('financialAudit.disposalDate')} />
                <ElectroNumberField
                  field={form.$('financialAudit.disposalPrice')}
                  prefix={getCurrencySymbol(
                    form.$('financialAudit.disposalCurrency').value,
                    activeUser.currency
                  )}
                  thousandSeparator={thousandSeparator}
                  decimalSeparator={decimalSeparator}
                />
                <ElectroSelect
                  field={form.$('financialAudit.disposalCurrency')}
                  model={this.currenciesModel}
                  simpleValue={true}
                />
                <ElectroSelect
                  field={form.$('financialAudit.auditedBy')}
                  model={this.financialAuditorsModel}
                  simpleValue={false}
                />
              </FormSection>
            )}
            {/* ================| ADDITIONAL INFORMATION |================ */}
            {(assetData.owned || typeof this.props.assetData === 'undefined') && (
              <FormSection form={form} section="additionalInformation">
                <ElectroTextField
                  field={this.form.$('additionalInformation.comments')}
                  props={{
                    multiline: true,
                    rows: 7,
                  }}
                />
              </FormSection>
            )}
            <FormControls
              buttonText={
                this.props.mode === 'edit'
                  ? t('general:actions.update')
                  : t('general:actions.create')
              }
              form={form}
            />
          </FormWrapper>
        </PageWrapper>
        <ActionDrawerWrapper
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              newManufacturer: (
                <AddNewManufacturer
                  parentForm={form}
                  accountsModel={accountsModel}
                  model={manufacturerModel}
                  childmodel={modelProduct}
                />
              ),
              newProduct: (
                <AddNewProduct
                  parentForm={form}
                  accountsModel={accountsModel}
                  manufacturerModel={manufacturerModel}
                  model={modelProduct}
                />
              ),
              newCompany: (
                <AddNewCompany
                  parentForm={form}
                  accountsModel={accountsModel}
                  model={companyModel}
                  childmodel={sitesModel}
                  resetModels={[sitesModel, buildingsModel, roomsModel, systemsModel]}
                  resetFields={[
                    form.$('location.locationSite'),
                    form.$('location.locationBuilding'),
                    form.$('location.locationRoom'),
                    form.$('location.locationSystem'),
                  ]}
                />
              ),
              newSite: (
                <AddNewSite
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={sitesModel}
                  childmodel={buildingsModel}
                  resetModels={[buildingsModel, roomsModel, systemsModel]}
                  resetFields={[
                    form.$('location.locationBuilding'),
                    form.$('location.locationRoom'),
                    form.$('location.locationSystem'),
                  ]}
                />
              ),
              newBuilding: (
                <AddNewBuilding
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={buildingsModel}
                  childmodel={roomsModel}
                  resetModels={[roomsModel, systemsModel]}
                  resetFields={[form.$('location.locationRoom'), form.$('location.locationSystem')]}
                />
              ),
              newRoom: (
                <AddNewRoom
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={roomsModel}
                  childmodel={systemsModel}
                  resetModels={[systemsModel]}
                  resetFields={[form.$('location.locationSystem')]}
                />
              ),
              newSystem: (
                <AddNewSystem
                  parentForm={form}
                  accountsModel={accountsModel}
                  companyModel={companyModel}
                  model={systemsModel}
                />
              ),
            }[this.props.actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
